import { useRef, useEffect, useState } from 'react';
import imagesLoaded from 'imagesloaded';
import findIndex from 'lodash/findIndex';

import usePrevious from '../../hooks/usePrevious';
import useMounted from '../../hooks/useMounted';

import styles from './ArtworkMasonry.css';

export default ({ loading, works }) => {
  const ref = useRef();
  const masonry = useRef();
  const itemRefs = useRef({});
  const previousWorks = usePrevious(works);
  const [initialLayoutComplete, setInitialLayoutComplete] = useState(false);
  const [displayable, setDisplayable] = useState(new Set());
  const mounted = useMounted();
  const waitingForImages = useRef(false);

  const hasWorks = Array.isArray(works) && works.length > 0;
  const hadWorks = Array.isArray(previousWorks) && previousWorks.length > 0;
  const displayIds = ids =>
    setDisplayable(prevDisplayable => new Set([...prevDisplayable, ...ids]));

  const worksChanged =
    hadWorks && hasWorks && works.length !== previousWorks.length;

  useEffect(() => {
    const initMasonry = () => {
      // eslint-disable-next-line global-require
      const Masonry = require('masonry-layout');

      // eslint-disable-next-line no-underscore-dangle
      const _masonry = new Masonry(ref.current, {
        itemSelector: `.${styles.gridItem}`,
        initLayout: false
      });

      const onLayoutComplete = () => {
        if (!initialLayoutComplete) setInitialLayoutComplete(true);
      };

      _masonry.on('layoutComplete', onLayoutComplete);

      return _masonry;
    };

    if (!loading && hasWorks && !masonry.current && !waitingForImages.current) {
      waitingForImages.current = true;
      imagesLoaded(`.${styles.gridItem}`, () => {
        waitingForImages.current = false;
        masonry.current = initMasonry();
        masonry.current.layout();
        displayIds(works.map(({ id }) => id));
      });
    }
  }, [works, loading, initialLayoutComplete]);

  useEffect(() => {
    if (worksChanged) {
      const previousLastWork = previousWorks[previousWorks.length - 1];
      const firstAppendedIndex =
        findIndex(works, ['id', previousLastWork.id]) + 1;
      const appendedWorks = works.slice(firstAppendedIndex);
      const appendedElements = appendedWorks.map(
        ({ id }) => itemRefs.current[id]
      );

      imagesLoaded(appendedElements, () => {
        masonry.current.appended(appendedElements);
        displayIds(appendedWorks.map(({ id }) => id));
      });
    }
  }, [works]);

  return {
    ref,
    displayable,
    itemRefs: itemRefs.current,
    initialLayoutComplete,
    mounted
  };
};
